/**
 * BiometricToggle — switch opt-in para biometria como 2º fator (HLOS-45).
 *
 * Aparece na AuthState (modo "locked") e no fluxo de setup inicial.
 * Quando habilitado, oferece unlock via biometric em vez de passphrase.
 *
 * Constitucional:
 * - CC-05 (zero penalidade): switch sem pressão; toggle off = voltar para
 *   passphrase sem lockout
 * - Princípio V (Aprendiz Decide): opt-in total
 * - CL-08 (Ética fechada): copy neutra, sem "garanta sua segurança"
 */

<script setup lang="ts">
import { computed, ref } from "vue";
import { useBiometricStore } from "../store";
import { storeToRefs } from "pinia";

/**
 * HLOS-173: master key de 32 bytes do vault unlocked — necessária para
 * setup biométrico real (cifrar com biometric-bound key).
 * O HomeState computa via `getVaultAPI().getDekBytes()` no onMounted.
 */
const props = defineProps<{ masterKey: Uint8Array | null }>();

const bio = useBiometricStore();
const { enabled, status, lastError } = storeToRefs(bio);

const isAvailable = computed(() => status.value === "available");
const isUnavailable = computed(() => status.value === "unavailable");

/**
 * HLOS-174: flag local para desabilitar checkbox enquanto a operação
 * (setup/disable) está em andamento. Previne race condition + checkbox
 * glitch visual (clique marca o checkbox imediatamente, mas o estado
 * `enabled` só atualiza após await setupBiometric).
 */
const toggling = ref(false);

/**
 * HLOS-173 + HLOS-174: toggle agora chama setupBiometric() / disableBiometric() reais
 * (era apenas marcador visual — HLOS-45 v0.5.0). Requer vault unlocked.
 * HLOS-174: `@change.prevent` evita que o browser altere visualmente o checkbox
 * antes da confirmação do setup — Vue controla via `:checked="enabled"`.
 */
async function onToggle(event: Event): Promise<void> {
  const target = event.target as HTMLInputElement;
  // Defesa em profundidade: garantir que o checkbox visual reflete `enabled`
  // (caso browser tenha aplicado change antes do `.prevent`).
  target.checked = enabled.value;

  if (toggling.value) return; // debounce
  toggling.value = true;
  try {
    if (enabled.value) {
      try {
        await bio.disableBiometric();
      } catch (e) {
        console.warn("[biometric] disableBiometric failed:", e);
        bio.recordFailure(String(e));
      }
      return;
    }
    if (!props.masterKey) {
      console.warn("[biometric] setupBiometric requires vault unlocked (masterKey ausente)");
      bio.recordFailure("masterKey ausente — destranque o vault primeiro");
      return;
    }
    try {
      await bio.setupBiometric(props.masterKey);
    } catch (e) {
      // HLOS-173 ressalva #1 (constitutional-guardian): distinguir cancel vs erro.
      // Cancelar = silent (CC-05 zero penalidade). Outros erros: log + recordFailure.
      const msg = e instanceof Error ? e.message : String(e);
      const isCancel = /cancel|cancelar|canceled/i.test(msg);
      if (!isCancel) {
        console.warn("[biometric] setupBiometric failed:", msg);
        bio.recordFailure(msg);
      } else {
        console.info("[biometric] setupBiometric canceled by user (CC-05 silent)");
      }
    }
  } finally {
    toggling.value = false;
    // Garantir sincronização final (caso store.enabled tenha mudado durante await).
    target.checked = enabled.value;
  }
}
</script>

<template>
  <div
    v-if="isAvailable || isUnavailable"
    class="hlos-biometric-toggle"
    data-testid="biometric-toggle"
  >
    <label class="hlos-biometric-toggle__label">
      <input
        type="checkbox"
        :checked="enabled"
        :disabled="isUnavailable || toggling"
        data-testid="biometric-toggle-input"
        @change.prevent="onToggle($event)"
      />
      <span class="hlos-biometric-toggle__text">
        Usar biometria para desbloquear (opcional)
      </span>
    </label>
    <p
      v-if="isUnavailable"
      class="hlos-biometric-toggle__hint"
      data-testid="biometric-toggle-unavailable"
    >
      Biometria indisponível neste dispositivo. Use passphrase.
    </p>
    <p
      v-if="lastError"
      class="hlos-biometric-toggle__hint hlos-biometric-toggle__hint--warn"
      role="alert"
      data-testid="biometric-toggle-error"
    >
      {{ lastError }}
    </p>
  </div>
</template>

<style scoped>
.hlos-biometric-toggle {
  margin-top: 1rem;
  padding: 0.5rem 0;
  border-top: 1px solid color-mix(in srgb, currentColor 15%, transparent);
}
.hlos-biometric-toggle__label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}
.hlos-biometric-toggle__text {
  font-size: 0.9em;
}
.hlos-biometric-toggle__hint {
  margin: 0.4rem 0 0;
  font-size: 0.85em;
  opacity: 0.7;
}
.hlos-biometric-toggle__hint--warn {
  color: var(--hlos-color-error); /* #993E2C — terracota profundo (AA 6.27:1) */
}
input[type="checkbox"] {
  cursor: pointer;
}
input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
